import { 
  Component, OnInit, Input, ElementRef, OnDestroy, 
  ChangeDetectionStrategy, ChangeDetectorRef, Output, 
  EventEmitter
} from '@angular/core';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

declare var $: any;
declare var Popper: any;

interface TestNode {
  name: string;
  children?: TestNode[];
  no_of_children: number;
  no_of_selected_children: number;
  col_name?: string;
  level?: string;
  highlight?: boolean;
  show?: boolean;
  all_children_visible?: boolean
  is_selected?: boolean;
}

@Component({
  selector: 'app-multi-select-nested-filter',
  templateUrl: './multi-select-nested-filter.component.html',
  styleUrls: ['./multi-select-nested-filter.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectNestedFilterComponent implements OnInit, OnDestroy {
  @Input() multi_select_param: any = {};
  @Input() unique_id: any;
  @Output() nested_multiselect_event: EventEmitter<any> = new EventEmitter<any>();
  @Output() multi_select_paramChange = new EventEmitter<any>();

  
  Object = Object;
  filter_value = '';
  treeControl = new NestedTreeControl<TestNode>(node => node.children);
  dataSource = new MatTreeNestedDataSource<TestNode>();

  selected_leaf_nodes: any = {};
  node_name_level_separator = '$$';
  global_data_json: TestNode[] = [];
  select_text = "Select All";
  expand_text = "Expand All";
  select_all_value = 0;
  is_expanded = false;
  window_scroll_top = 0;
  filterSubject = new Subject<string>();
  destroy$ = new Subject<void>();
  update_position_request_id: number | null = null;
  popperInstance: any = null;
  lastDimensions = { dropdown_width: 0, dropdown_height: 0, menu_width: 0, menu_height: 0 };
  @Input() style_conf?: any = {
    'default_font_size': 14,
  };
  constructor(private cdRef: ChangeDetectorRef) {}

  hasChild = (_: number, node: TestNode) => !!node.children && node.children.length > 0;

  ngOnInit() {
    this.initialize_data();
    this.setup_filter_subscription();
    this.setup_scroll_listener();
  }

  ngOnDestroy() {
    this.cleanup();
  }

  initialize_data() {
    this.build_node_lookup();
    this.dataSource.data = this.multi_select_param.filterList || [];
    this.treeControl.dataNodes = this.dataSource.data;
    this.select_all_value = this.check_all_selected()
    this.select_text = this.select_all_value ? "Deselect All" : "Select All";
  }

  build_node_lookup() {
    const traverse = (nodes: TestNode[], parentPath: string = '') => {
      nodes.forEach((node, index) => {
        node.no_of_children = (node.children && node.children.length)?node.children.length:1
        if(node.no_of_selected_children == undefined){
          node.no_of_selected_children = this.multi_select_param.mandatory?node.no_of_children:0
        }
        if(!node.children.length && node.no_of_selected_children == node.no_of_children){
          let nodeKey = this.get_node_key(node)
          this.selected_leaf_nodes[nodeKey] = node;
        }
        if (node.children.length) {
          traverse(node.children);
        }
      });
    };
    traverse(this.multi_select_param.filterList);
  }

  get_node_key(node: TestNode): string {
    return `${node.name}${this.node_name_level_separator}${node.level}`;
  }

  setup_filter_subscription() {
    this.filterSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(value => this.filter_changed(value));
  }

  setup_scroll_listener() {
    window.addEventListener('scroll', this.on_window_scroll, { passive: true });
  }

  cleanup() {
    window.removeEventListener('scroll', this.on_window_scroll);
    this.destroy$.next();
    this.destroy$.complete();
    if (this.update_position_request_id) {
      cancelAnimationFrame(this.update_position_request_id);
    }
  }
  
  // This function bind to click event beacuse during search if we click on any parent node which is partial selected, and after 
  // selection if it should again be partial selected, so we don't have to toggle the parent selection again.
  // If we bind this function to change event then it will toggle the parent selection automatically if we click on parent node.
  // So we bind this function to click event and stop propagation and prevent default to avoid the default behavior of checkbox to automatically change the checkbox state if it is not changed in the condition
  toggle_parent_selection(event: Event, node: TestNode) {
    event.stopPropagation();
    event.preventDefault();
    const isChecked = node.no_of_selected_children !== node.no_of_children && !node.is_selected;
    node.is_selected = !node.is_selected;
    this.toggle_selection(isChecked, node);
  }

  toggle_selection(isChecked: boolean, node: TestNode, updateParent = true) {
    const nodeKey = this.get_node_key(node);
    if (!node.show && this.filter_value !== '') return 0;
    if (isChecked && node.no_of_selected_children === node.no_of_children) return 0;
    if (!isChecked && node.no_of_selected_children === 0) return 0;

    const old_value = node.no_of_selected_children;

    if (this.filter_value !== '' && !node.all_children_visible) {
      let parent_value = 0;
      if (node.children && node.children.length) {
        for(let i=0;i<node.children.length;i++){
          if(node.children[i].show){
            parent_value = parent_value + this.toggle_selection(isChecked, node.children[i], false);
          }
        }
        node.no_of_selected_children = node.no_of_selected_children + parent_value;
      }else{
        node.no_of_selected_children = isChecked ? node.no_of_children : 0;
      }
    } else {
      node.no_of_selected_children = isChecked ? node.no_of_children : 0;

      if (node.children && node.children.length) {
        node.children.forEach(child => this.toggle_selection(isChecked, child, false));
      }
    }

    if (!node.children || node.children.length === 0) {
      if (isChecked) {
        this.selected_leaf_nodes[nodeKey] = node;
      } else if (this.selected_leaf_nodes && this.selected_leaf_nodes[nodeKey]) {
        delete this.selected_leaf_nodes[nodeKey];
      }
    }
    const new_value = this.get_parent_node_value(isChecked, old_value, node);

    // Update parents
    if (updateParent && node.level) {
      const new_value = this.get_parent_node_value(isChecked, old_value, node);
      if (new_value) {
        this.update_parent_nodes(isChecked, node.level.split('_').slice(0, -1), new_value);
      }
    }

    if (updateParent) {
      this.select_all_value = this.check_all_selected();
      this.select_text = this.select_all_value ? "Deselect All" : "Select All";
      this.multi_select_paramChange.emit(this.multi_select_param);
      this.cdRef.markForCheck();
      this.cdRef.detectChanges();
    }
    return new_value;
  }

  get_parent_node_value(isChecked, old_value, node){
    let new_value = 0
      if(isChecked){
        if(!old_value){
          if(node.no_of_selected_children == node.no_of_children){
            new_value = 1
          }else{
            new_value = 0.5
          }
        }else{
          if(node.no_of_selected_children == node.no_of_children){
            new_value = 0.5
          }
        }
      }else{
        if(old_value == node.no_of_children){
          if(!node.no_of_selected_children){
            new_value = -1
          }else{
            new_value = -0.5
          }
        }else{
          if(old_value == 0){
            new_value = -0.5
          }
        }
      }
      return new_value
  }

  update_parent_nodes(isChecked, level_list, new_value){
    if(level_list.length == 0){
      return;
    }
    let node_list = this.dataSource.data;
    let current_node: TestNode = null;
    let current_level = node_list;
    for (let i = 0; i < level_list.length; i++) {
      const index = level_list[i];
      current_node = current_level[index];
      current_level = current_node.children || [];
    }
    let old_value = current_node.no_of_selected_children
    current_node.no_of_selected_children = current_node.no_of_selected_children + new_value
    if(level_list.length>1){
      new_value = this.get_parent_node_value(isChecked, old_value, current_node)
      if(!new_value){
        return;
      }
      this.update_parent_nodes(isChecked, level_list.slice(0, -1), new_value)
    }
  }

  on_filter_input(event: Event) {
    this.filterSubject.next((event.target as HTMLInputElement).value);
  }

  filter_changed(inputValue: string) {
    const search = inputValue.toLowerCase() || '';
    if (search.trim() == ''){
      return
    }

    this.multi_select_param.filterList.forEach(node =>
      this.apply_filter_in_place(node, search, false)
    );

    this.dataSource.data = this.multi_select_param.filterList;
    this.treeControl.dataNodes = this.dataSource.data;
    this.treeControl.expandAll();
    this.schedule_position_update();
    this.cdRef.markForCheck();
  }

  apply_filter_in_place(node: TestNode, word: string, parentMatched: boolean): boolean {
    const nodeMatched = (node.name || '').toLowerCase().includes(word);

    node.highlight = false;
    node.show = false;
    node.all_children_visible = false;

    let anyChildMatched = false;
    let all_children_visible = true;

    if (node.children && node.children.length) {
      for (const child of node.children) {
        const childMatched = this.apply_filter_in_place(child, word, nodeMatched || parentMatched);
        anyChildMatched = anyChildMatched || childMatched;
        if (!child.show) {
          all_children_visible = false;
        }
      }
    } else {
      // No children means "all children visible" is trivially true
      all_children_visible = true;
    }

    if (nodeMatched) {
      node.highlight = true;
    }

    node.show = nodeMatched || anyChildMatched || parentMatched;
    node.all_children_visible = all_children_visible;

    return node.show;
  }

  clear_search() {
    this.filter_value = '';
    if (this.is_expanded) {
      this.treeControl.expandAll();
    } else {
      this.treeControl.collapseAll();
    }
    this.schedule_position_update();
    this.cdRef.markForCheck();
  }

  schedule_position_update() {
    if (this.update_position_request_id) {
      cancelAnimationFrame(this.update_position_request_id);
    }
    this.update_position_request_id = requestAnimationFrame(() => this.update_dropdown_position());
  }

  update_dropdown_position() {
    const element = document.getElementById(this.unique_id + 'multi_select_dropdown');
    const dropdown = document.getElementById(this.unique_id + 'resizableDiv');
    
    if (!element || !dropdown) return;

    const dropdown_menu_dimension = dropdown.getBoundingClientRect();
    const element_dimension = element.getBoundingClientRect();
    
    const dimChanged = 
      dropdown_menu_dimension.width !== this.lastDimensions.menu_width ||
      dropdown_menu_dimension.height !== this.lastDimensions.menu_height ||
      element_dimension.width !== this.lastDimensions.dropdown_width ||
      element_dimension.height !== this.lastDimensions.dropdown_height;
    
    if (!dimChanged) return;

    this.lastDimensions = {
      menu_width: dropdown_menu_dimension.width,
      menu_height: dropdown_menu_dimension.height,
      dropdown_width: element_dimension.width,
      dropdown_height: element_dimension.height
    };

    const is_dropdown_out_of_window = (element_dimension.left + dropdown_menu_dimension.width) >= window.innerWidth;

    if (element_dimension.bottom + 280 >= window.innerHeight) {
      document.body.style.height = `${element_dimension.bottom + dropdown_menu_dimension.height + 50}px`;
      document.body.style.overflowY = 'auto';
    }

    if (typeof Popper !== 'undefined') {
      if (!this.popperInstance) {
        this.popperInstance = new Popper(element, dropdown, {
          placement: is_dropdown_out_of_window ? 'bottom-end' : 'bottom-start',
          modifiers: {
            flip: { enabled: false },
            preventOverflow: { boundariesElement: 'viewport' }
          },
          strategy: 'fixed'
        });
      } else {
        this.popperInstance.options.placement = is_dropdown_out_of_window ? 'bottom-end' : 'bottom-start';
        this.popperInstance.scheduleUpdate();
      }
    }
  }

  toggle_all_selection(isChecked: boolean, force_removed: boolean = false) {
    if (force_removed) {
      this.clear_search();
    }
    for(let i=0;i<this.dataSource.data.length;i++){
      this.toggle_selection(isChecked, this.dataSource.data[i]);
    }
    this.select_text = isChecked ? "Deselect All" : "Select All";
    this.select_all_value = isChecked ? 1 : 0;
    this.schedule_position_update();
    this.cdRef.markForCheck();
  }

  expand_all(isExpanded: boolean) {
    if (isExpanded) {
      this.treeControl.expandAll();
      this.expand_text = "Collapse All";
    } else {
      this.treeControl.collapseAll();
      this.expand_text = "Expand All";
    }
    this.is_expanded = isExpanded;
    this.schedule_position_update();
    this.cdRef.markForCheck();
  }

  on_window_scroll() {
    const dropdown = document.getElementById(`${this.unique_id}resizableDiv`);
    if (dropdown && dropdown.classList.contains('show')) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (scrollTop < this.window_scroll_top) {
        $(`#${this.unique_id}multi_select_dropdown`).dropdown('toggle');
      }
      this.window_scroll_top = scrollTop;
    }
  }

  get_first_three_selected_items() {
    return Object.entries(this.selected_leaf_nodes)
      .slice(0, 3)
      .map(([key, value]) => ({ key, value }));
  }

  create_nested_multiselect_payload(current_list: any[] = [], temp_list: any[] = [], node_list: TestNode[] = this.multi_select_param.filterList): any[][] {
    for (let node of node_list) {
      const isSelected = node.no_of_selected_children > 0;
      if (isSelected) {
        temp_list.push({
          name: node.col_name,
          value: node.name
        });

        if (node.children && node.children.length) {
          [current_list, temp_list] = this.create_nested_multiselect_payload(current_list, temp_list, node.children);
        } else {
          current_list.push([...temp_list]);
        }

        temp_list.pop();
      }
    }
    return [current_list, temp_list];
  }

  get hasSelections(): boolean {
    return this.selected_leaf_nodes ? Object.keys(this.selected_leaf_nodes).length > 0: false;
  }

  check_all_selected(){
    for(let i=0; i<this.dataSource.data.length; i++){
      if(this.filter_value == ''){
        if(this.dataSource.data[i].no_of_children != this.dataSource.data[i].no_of_selected_children){
          return 0;
        }
      }else{
        if(this.dataSource.data[i].show){
          if(this.dataSource.data[i].all_children_visible && this.dataSource.data[i].no_of_children != this.dataSource.data[i].no_of_selected_children){
            return 0;
          }else{
            for(let j=0; j<this.dataSource.data[i].children.length; j++){
              if(this.dataSource.data[i].children[j].show && this.dataSource.data[i].children[j].no_of_children != this.dataSource.data[i].children[j].no_of_selected_children){
                return 0;
              }
            }
          }
        }
      }
    }
    return 1;
  }
}
